import type { UserAiMessageAction } from '@entities/user';
import { Anchor, Button, Flex, Loader, Text } from '@mantine/core';
import {
  ChatBubble,
  ChatBubbleHeader,
  ChatListenButton,
  ChatMessageMeta,
  ChatMessageTime,
  ChatSender,
  type ChatSide,
} from '@packages/chat/ui';
import { MarkdownContent } from '@shared/ui/MarkdownContent';
import { memo } from 'react';
import { NavLink } from 'react-router';
import {
  getVacancyLinkFromActions,
  getVacancyButtonsFromActions,
} from './lib/getVacancyLinkFromActions';

export interface UserSintiaChatReadonlyMessageProps {
  time: string;
  message: string | null;
  fileName: string;
  senderName?: string;
  isAi?: boolean;
  contentType: string;
  handleOpenModal: (audioUrl: string, fileName: string) => void;
  audioUrl: string;
  sender: ChatSide;
  status?: string;
  transcription?: string | null;
  actions?: UserAiMessageAction | null;
}

export const UserSintiaChatReadonlyMessage = memo(
  ({
    time,
    sender,
    message,
    contentType,
    audioUrl,
    fileName,
    handleOpenModal,
    senderName,
    isAi = false,
    status,
    transcription,
    actions,
  }: UserSintiaChatReadonlyMessageProps) => {
    const isFailed = status === 'Error';
    const isPending = status === 'Pending';
    const vacancyButtons = getVacancyButtonsFromActions(actions);
    const vacancyLink =
      vacancyButtons.length === 0 ? getVacancyLinkFromActions(actions) : null;

    return (
      <ChatBubble side={sender}>
        <ChatBubbleHeader side={sender}>
          {isPending && (
            <Loader style={{ alignSelf: 'center' }} size='sm' color='blue' />
          )}
          <Flex gap={8} align='center'>
            <ChatListenButton
              label='Слушать'
              audioUrl={audioUrl || undefined}
              onListen={url => handleOpenModal(url, fileName)}
            />
          </Flex>

          <ChatMessageMeta side={sender}>
            <ChatMessageTime time={time} />
            <ChatSender
              name={senderName}
              avatarSrc={isAi ? '/cynthia-chat-avatar.svg' : undefined}
              avatarPosition='end'
            />
          </ChatMessageMeta>
        </ChatBubbleHeader>
        {transcription && <Text size='sm'>{transcription}</Text>}
        {contentType === 'Text' && message && (
          <MarkdownContent content={message} />
        )}
        {vacancyButtons.map((button, index) => (
          <Button
            key={`${button.id}:${index}`}
            component={NavLink}
            to={`/vacancy-list/${button.id}`}
            size='md'
            w={{ base: '100%', sm: 'fit-content' }}
            maw='100%'
          >
            {button.label}
          </Button>
        ))}
        {vacancyLink && (
          <Anchor
            component={NavLink}
            to={`/vacancy-list/${vacancyLink.id}`}
            size='md'
            fw={600}
            c='secondary.7'
          >
            {`«${vacancyLink.id}. ${vacancyLink.name}»`}
          </Anchor>
        )}
        {isFailed && (
          <Text size='sm' c='red'>
            Сообщение не доставлено
          </Text>
        )}
      </ChatBubble>
    );
  }
);
